// ---------------------------------------------------------------------------
// The lab's door to the prototype's backend (Firestore + Cloud Functions)
// ---------------------------------------------------------------------------
//
// Every read and write of the progression prototype goes through here, and
// everything it hands back is plain data (documents' JSON strings parsed):
//   players/{uid}                  the player's progression state (read only)
//   wars/{id}                      a war's public document
//   wars/{id}/views/{seat}         the player's own view of the war
//   wars/{id}/views/{seat}/reveals/{n}  their reveal of round n ('end' at the end)
//   wars/{id}/submissions/{n}_{seat}    their orders for round n (write once)
// and the callables in functions/src (players.js, wars.js, timers.js), which
// make every change. The rules decide; the server applies; this module only
// carries. (functions/src/wars.js documents the server side.)

import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions'
import { connectFirestoreEmulator, doc, onSnapshot, collection, query, where, limit, setDoc, getDoc } from 'firebase/firestore'
import { app, USE_EMULATORS } from '../FirebaseConfig'
import { getGameFirestore } from '../FirestoreConfig'

let services = null
function backend() {
    if (services) return services
    const firestore = getGameFirestore(app)
    const functions = getFunctions(app)
    if (USE_EMULATORS) {
        connectFirestoreEmulator(firestore, '127.0.0.1', 8080)
        connectFunctionsEmulator(functions, '127.0.0.1', 5001)
    }
    services = { firestore, functions }
    return services
}

// A callable's result, or a thrown Error carrying the server's message, its
// code ('failed-precondition', …) and any details it attached (the
// placement step's list, for a Hive that isn't ready for war).
async function call(name, data) {
    try {
        return (await httpsCallable(backend().functions, name)(data ?? {})).data
    } catch (e) {
        const err = new Error(e.message || 'Something went wrong.')
        err.code = String(e.code ?? '').replace(/^functions\//, '')
        err.details = e.details ?? null
        throw err
    }
}

// ── Player ──────────────────────────────────────────────────────────────────

export const loadPlayer = () => call('playerState')
export const openGenesisChest = () => call('genesisOpen')
export const placeLot = (cell, type) => call('hiveEdit', { op: 'placeLot', cell, type })
export const removeLot = (cell) => call('hiveEdit', { op: 'removeLot', cell })
export const placeStructure = (name, cell, rotation = 0) => call('hiveEdit', { op: 'placeStructure', name, cell, rotation })
export const removeStructure = (name) => call('hiveEdit', { op: 'removeStructure', name })
export const startUpgrade = (name) => call('upgradeStart', { name })
export const finishUpgradeNow = (name) => call('upgradeFinishNow', { name })

// The player's document as it changes. Returns an unsubscribe function.
export function watchPlayer(uid, onChange) {
    return onSnapshot(doc(backend().firestore, `players/${uid}`), (snap) => onChange(snap.exists() ? snap.data() : null))
}

// ── Wars ────────────────────────────────────────────────────────────────────

// `generated`: a test war on generated Hives of that many structures.
export const startBotWar = (generated = null) => call('warStartBot', generated ? { generated } : {})
export const createChallenge = (mode, generated = null) => call('challengeCreate', generated ? { mode, generated } : { mode })
export const acceptChallenge = (code) => call('challengeAccept', { code })
export const revealWatched = (warId) => call('revealWatched', { warId })
export const catchUpWar = (warId) => call('warCatchUp', { warId })

// The player's wars (newest first), as they change.
export function watchMyWars(uid, onChange) {
    const q = query(collection(backend().firestore, 'wars'), where('participants', 'array-contains', uid), limit(100))
    return onSnapshot(q, (snap) => {
        const wars = snap.docs.map(d => d.data()).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
        onChange(wars)
    })
}

export function watchWar(warId, onChange) {
    return onSnapshot(doc(backend().firestore, `wars/${warId}`), (snap) => onChange(snap.exists() ? snap.data() : null))
}

// The player's view of the war ({ ...view, lastError }), as it changes.
export function watchView(warId, seat, onChange) {
    return onSnapshot(doc(backend().firestore, `wars/${warId}/views/${seat}`), (snap) => {
        if (!snap.exists()) return onChange(null)
        const d = snap.data()
        onChange({ ...JSON.parse(d.json), lastError: d.lastError ?? null })
    })
}

// One reveal (round number, or 'end'), or null if it isn't there (yet).
export async function loadReveal(warId, seat, n) {
    const snap = await getDoc(doc(backend().firestore, `wars/${warId}/views/${seat}/reveals/${n}`))
    return snap.exists() ? JSON.parse(snap.data().json) : null
}

// The player's orders for the open round. Written once; the server answers
// through the view (the round moves on, or lastError says why not).
export function submitOrders({ warId, seat, round, uid, orders }) {
    return setDoc(doc(backend().firestore, `wars/${warId}/submissions/${round}_${seat}`), {
        uid, seat, round, orders: JSON.stringify(orders),
    })
}
